import { business } from "@/config/business";
import { SITE_ORIGIN } from "@/lib/page-meta";

const siteOrigin = SITE_ORIGIN;

type RoofingContractorJsonLd = {
  "@context": "https://schema.org";
  "@type": "RoofingContractor";
  "@id": string;
  name: string;
  url: string;
  logo: string;
  telephone?: string;
  email?: string;
  description?: string;
  foundingDate?: string;
  brand?: {
    "@type": "Brand";
    name: string;
  };
  parentOrganization?: {
    "@type": "Organization";
    "@id"?: string;
    name: string;
    url: string;
  };
  areaServed?: Array<{ "@type": "City"; name: string }>;
  address?: {
    "@type": "PostalAddress";
    addressLocality: string;
    addressRegion: string;
    addressCountry: string;
  };
};

/**
 * Conservative RoofingContractor node from verified `business` config only.
 * Omits street address, geo, opening hours, price range, ratings, reviews,
 * and hasCredential (no invented licence / WCB / RCABC IDs).
 */
type WebSiteJsonLd = {
  "@context": "https://schema.org";
  "@type": "WebSite";
  "@id": string;
  name: string;
  url: string;
  inLanguage: string[];
  publisher: {
    "@type": "RoofingContractor";
    name: string;
    url: string;
  };
};

type WebPageJsonLd = {
  "@context": "https://schema.org";
  "@type": "WebPage";
  "@id": string;
  url: string;
  name: string;
  description: string;
  inLanguage: string;
  image?: string;
  isPartOf: {
    "@type": "WebSite";
    "@id": string;
    name: string;
    url: string;
  };
  about: {
    "@type": "RoofingContractor";
    "@id": string;
    name: string;
    url: string;
  };
};

type FaqPageJsonLd = {
  "@context": "https://schema.org";
  "@type": "FAQPage";
  mainEntity: Array<{
    "@type": "Question";
    name: string;
    acceptedAnswer: {
      "@type": "Answer";
      text: string;
    };
  }>;
};

type ArticleJsonLd = {
  "@context": "https://schema.org";
  "@type": "Article";
  headline: string;
  description: string;
  datePublished: string;
  dateModified: string;
  image?: string;
  author: {
    "@type": "Organization";
    name: string;
    url: string;
  };
  publisher: {
    "@type": "Organization";
    name: string;
    url: string;
    logo: {
      "@type": "ImageObject";
      url: string;
    };
  };
  mainEntityOfPage: string;
};

export function buildArticleJsonLd(options: {
  url: string;
  headline: string;
  description: string;
  datePublished: string;
  dateModified: string;
  image?: string;
}): ArticleJsonLd {
  const node: ArticleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: options.headline,
    description: options.description,
    datePublished: options.datePublished,
    dateModified: options.dateModified,
    author: {
      "@type": "Organization",
      name: business.name,
      url: business.url,
    },
    publisher: {
      "@type": "Organization",
      name: business.name,
      url: business.url,
      logo: {
        "@type": "ImageObject",
        url: `${business.url}/RevolinGroup-new-logo.png`,
      },
    },
    mainEntityOfPage: options.url,
  };
  if (options.image) node.image = options.image;
  return node;
}

export function buildFaqPageJsonLd(
  items: readonly { question: string; answer: string }[],
): FaqPageJsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

type ServiceJsonLd = {
  "@context": "https://schema.org";
  "@type": "Service";
  "@id": string;
  url: string;
  name: string;
  description: string;
  serviceType: string;
  inLanguage: string;
  provider: {
    "@type": "RoofingContractor";
    "@id": string;
    name: string;
    url: string;
  };
  areaServed: Array<{
    "@type": "AdministrativeArea";
    name: string;
  }>;
};

/**
 * WebSite node for the home page. `inLanguage` lists the BCP-47 tags of the
 * locales the site actually publishes (passed in by the caller so this module
 * stays free of i18n imports). No SearchAction — the site has no search box.
 */
export function buildWebSiteJsonLd(inLanguage: string[]): WebSiteJsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${siteOrigin}/#website`,
    name: business.name,
    url: business.url,
    inLanguage,
    publisher: {
      "@type": "RoofingContractor",
      name: business.name,
      url: business.url,
    },
  };
}

/** WebPage node for indexable content pages, linked to the one verified business. */
export function buildWebPageJsonLd(options: {
  url: string;
  name: string;
  description: string;
  inLanguage: string;
  image?: string;
}): WebPageJsonLd {
  const node: WebPageJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${options.url}#webpage`,
    url: options.url,
    name: options.name,
    description: options.description,
    inLanguage: options.inLanguage,
    isPartOf: {
      "@type": "WebSite",
      "@id": `${siteOrigin}/#website`,
      name: business.name,
      url: business.url,
    },
    about: {
      "@type": "RoofingContractor",
      "@id": `${siteOrigin}/#org`,
      name: business.name,
      url: business.url,
    },
  };

  if (options.image) node.image = options.image;
  return node;
}

/**
 * Service node for real service hubs. `areaServed` is an operating area only;
 * it deliberately contains no address, branch, geo point or fabricated office.
 */
export function buildServiceJsonLd(options: {
  url: string;
  name: string;
  description: string;
  serviceType: string;
  inLanguage: string;
  areaServed: readonly string[];
}): ServiceJsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${options.url}#service`,
    url: options.url,
    name: options.name,
    description: options.description,
    serviceType: options.serviceType,
    inLanguage: options.inLanguage,
    provider: {
      "@type": "RoofingContractor",
      "@id": `${siteOrigin}/#org`,
      name: business.name,
      url: business.url,
    },
    areaServed: options.areaServed.map((name) => ({
      "@type": "AdministrativeArea",
      name,
    })),
  };
}

export function buildRoofingContractorJsonLd(options?: {
  description?: string;
}): RoofingContractorJsonLd {
  const node: RoofingContractorJsonLd = {
    "@context": "https://schema.org",
    "@type": "RoofingContractor",
    "@id": `${siteOrigin}/#org`,
    name: business.name,
    url: business.url,
    logo: `${business.url}/RevolinGroup-new-logo.png`,
    brand: {
      "@type": "Brand",
      name: business.name,
    },
  };

  if (business.phone) node.telephone = business.phone;
  if (business.email) node.email = business.email;
  if (options?.description) node.description = options.description;
  if (business.establishedYear) {
    node.foundingDate = String(business.establishedYear);
  }

  if (business.corporateParentUrl) {
    node.parentOrganization = {
      "@type": "Organization",
      "@id": business.corporateParentId,
      name: business.corporateParentName,
      url: business.corporateParentUrl,
    };
  }

  if (business.serviceAreas.length) {
    node.areaServed = business.serviceAreas.map((city) => ({
      "@type": "City",
      name: city,
    }));
  }

  if (business.address?.locality && business.address?.region && business.address?.country) {
    node.address = {
      "@type": "PostalAddress",
      addressLocality: business.address.locality,
      addressRegion: business.address.region,
      addressCountry: business.address.country,
    };
  }

  return node;
}
